// QR Kiosk v2 — §8.3 pure thread-detail mapper. COPY-NOT-IMPORT of
// mapThreadDetailMessages + helpers (parseArtifacts / parseSources / mapSources /
// ensureUtcSuffix / msg-id fallback) from customer/midas/midasPageSlice.ts:63-184
// (a Redux slice — never import a slice across sections; §0 copy policy). Only
// mapBotArtifacts is reused from the already-v2-approved shared/chatBot graph.
// Pure: no React, no Redux, no MUI. Restores text, sources, artifacts, and the
// user-image URL on thread reload so a re-opened conversation renders identically.
import { mapBotArtifacts } from "../../../shared/chatBot/streamBotMessage";
import type { ChatMessage, ChatSource, ChatArtifact } from "../../../shared/chatBot/models/chatBotModels";
import type { ThreadDetailResponse } from "../../../customer/midas/midasPageModels";

const generateMsgId = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const mapSources = (sources?: any[]): ChatSource[] | undefined => {
  if (!sources || sources.length === 0) return undefined;
  return sources.map((s: any) => ({
    document: s.document,
    page: s.page,
    pageFrom: s.page_from ?? s.pageFrom,
    pageTo: s.page_to ?? s.pageTo,
    url: s.url,
    imageUrl: s.image_url ?? s.imageUrl,
  }));
};

/** Append "Z" so a suffix-less UTC datetime parses as UTC (not local). */
export const ensureUtcSuffix = (iso: string): string => {
  if (!iso) return iso;
  if (iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso)) return iso;
  return iso + "Z";
};

const parseSources = (raw: string | null | undefined): any[] | undefined => {
  if (!raw) return undefined;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : undefined;
  } catch {
    return undefined;
  }
};

const parseArtifacts = (raw: string | null | undefined): ChatArtifact[] | undefined => {
  if (!raw) return undefined;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? mapBotArtifacts(arr) : undefined;
  } catch {
    return undefined;
  }
};

export const mapKioskThreadMessages = (detail: ThreadDetailResponse): ChatMessage[] =>
  detail.messages.map((m) => {
    const artifacts = parseArtifacts(m.artifacts);
    const userImage = m.role === "user" ? artifacts?.find((a) => a.type === "user_image") : undefined;
    const rawText = (m.content ?? "").trim();
    // Legacy "[image]" placeholder (pre-user_image bot builds) → empty.
    const text = rawText === "[image]" ? "" : rawText;
    return {
      id: m.id || generateMsgId(),
      role: m.role,
      text,
      timestamp: ensureUtcSuffix(m.created_at),
      sources: mapSources(parseSources(m.sources)),
      artifacts,
      imageUrl: userImage?.url,
      imageName: userImage?.title,
    };
  });
